import {
  Box,
  Card,
  CardBody,
  Grid,
  GridItem,
  IconButton,
  Flex,
  Button,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, Plus } from 'lucide-react';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { SlideKind } from '@duke-hq/svc-api-db';
import { unitAssessmentState } from '../UnitAssessmentState';
import { UnitAssessmentSlides } from '../types';
import { QuestionGroupItem } from './QuestionGroupItem';
import { useSnapshot } from 'valtio';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

const MotionBox = motion.create(Box);

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
};

type QuestionGroupPropTypes = {
  slide: Extract<UnitAssessmentSlides[0], { kind: typeof SlideKind.QUESTION_GROUP }>;
  handleDeleteMultiChoiceQuestion: (id: string) => void;
};

export function QuestionGroup({ slide, handleDeleteMultiChoiceQuestion }: QuestionGroupPropTypes) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const unitAssessmentStateSnap = useSnapshot(unitAssessmentState);
  const reorderOptions = trpcAdmin.unitAssessment.reorderMultiChoiceQuestionOptions.useMutation({
    onSuccess: () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
    },
  });

  const handleAddMultiChoiceQuestion = (index: number) => {
    unitAssessmentState.setCurrentSlide(slide);
    unitAssessmentState.setCurrentMultiChoiceQuestionIndex(index);
    unitAssessmentState.setEditorType('MULTI_CHOICE_QUESTION');
    unitAssessmentState.setEditorMode(EditorMode.CREATE);
    unitAssessmentState.openEditor();
  };

  const handlePrev = () => {
    setDirection(-1);
    if (index !== 0) {
      setIndex((old) => old - 1);
      if (unitAssessmentStateSnap.isEditorOpen) {
        unitAssessmentState.setCurrentMultiChoiceQuestionIndex(index - 1);
        unitAssessmentState.setEditorMode(EditorMode.EDIT);
      }
    }
  };

  const handleNext = () => {
    setDirection(1);
    if (index !== slide.questions.length) {
      setIndex((old) => old + 1);
      if (unitAssessmentStateSnap.isEditorOpen) {
        unitAssessmentState.setCurrentMultiChoiceQuestionIndex(index + 1);
        if (index + 1 === slide.questions.length) {
          handleAddMultiChoiceQuestion(index + 1);
        } else {
          unitAssessmentState.setEditorMode(EditorMode.EDIT);
        }
      }
    }
  };

  const currentQuestion = slide.questions[index];

  return (
    <Box>
      <Grid templateColumns="repeat(8, 1fr)" gap={6}>
        <GridItem w="100%" h="full">
          <Flex justifyContent="flex-end" alignItems="center" h={'full'}>
            <Tooltip label={t`Previous Question`}>
              <IconButton
                variant="tertiary"
                icon={<ArrowLeftIcon />}
                aria-label={t`Previous`}
                onClick={handlePrev}
                hidden={index === 0}
              />
            </Tooltip>
          </Flex>
        </GridItem>

        <GridItem colSpan={6} w="100%" h="full">
          <AnimatePresence initial={false} custom={direction}>
            <Box w={'full'}>
              <MotionBox
                key={index}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2, ease: 'linear' },
                }}
                style={{ width: '100%' }}
              >
                {!currentQuestion ? (
                  <Card bg={'bg.canvas'}>
                    <CardBody>
                      <Flex
                        direction={'column'}
                        h={'400px'}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Button
                          leftIcon={<Icon as={Plus} boxSize="24px" />}
                          variant="tertiary"
                          onClick={() => {
                            handleAddMultiChoiceQuestion(index);
                          }}
                        >
                          <Trans>Add Question</Trans>
                        </Button>
                      </Flex>
                    </CardBody>
                  </Card>
                ) : (
                  <QuestionGroupItem
                    index={index}
                    questionObject={currentQuestion}
                    handleDeleteMultiChoiceQuestion={handleDeleteMultiChoiceQuestion}
                    slide={slide}
                    reorderOptions={reorderOptions}
                  />
                )}
              </MotionBox>
            </Box>
          </AnimatePresence>
        </GridItem>

        <GridItem w="100%" h="full">
          <Flex justifyContent="flex-start" alignItems="center" h={'full'}>
            <Tooltip label={t`Next Question`}>
              <IconButton
                variant="tertiary"
                icon={<ArrowRightIcon />}
                aria-label={t`Next`}
                onClick={handleNext}
                hidden={
                  index >= 2 || slide.questions.length === 0 || index === slide.questions.length
                }
              />
            </Tooltip>
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
}
